import { isValidElement, type ReactNode } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { Commodity, Process, Segment } from "@/types/contract";
import type { Contract } from "@/types/contract";
import type { Product, Process as ProcessEnumeration } from "@/types/enumerations";
import type { CustomerType } from "@/types/customerType";
import { CompanyTypeValues } from "@/constants/customer";
import { getCurrentProductName } from "@/utils/product";

// ─── Mandatory page attributes (DL-1 … DL-16) ───────────────────────────────

export type DataLayerSegment = "VYROBCI" | "MOO" | "MOP";
export type DataLayerProductType = "Elektřina" | "Plyn";
export type DataLayerEnvironment = "PROD" | "PREPROD" | "TEST" | "DEV" | "DEV2";
export type DataLayerCustomerType = "MOO" | "MOP";
export type DataLayerCustomerSubType = "Company" | "Enterpreneur";

export type MandatoryAttributes = {
  pageName: string;
  pageTitle: string;
  language: "cs";
  segment?: DataLayerSegment;
  category: "eContracts FE";
  productType?: DataLayerProductType;
  product?: string;
  hierarchyUrl: string[];
  timestamp: string;
  application: "eContracts FE";
  environment: DataLayerEnvironment;
  buildVersion: string;
};

// ─── Form tracking (§ 3.1) ────────────────────────────────────────────────────

const FORM_STEPS = {
  "/welcome": "Uvítání",
  "/ocr": "OCR",
  "/customer": "Osobní a kontaktní údaje",
  "/supply-point": "Odběrné místo",
  "/supplier": "Současný dodavatel",
  "/payments": "Platby",
  "/recapitulation": "Shrnutí",
  "/agreements": "GDPR souhlas",
  "/view": "Zobrazení dokumentů k podpisu",
  "/signature": "Podpis",
  "/sms-code": "SMS kód",
  "/thank-you": "Poděkování",
  "/sending-on-phone": "Předání k podpisu na telefonu",
  "/signature-on-phone": "Podpis na telefonu",
  "/attachments": "Doplňování příloh",
  "/add-information": "Doplnění informací",
  "/supply-point-confirm": "Akceptace přepisu",
  "/previous-customer": "Stávající zákazník",
  "/download-documents": "Stažení smluvní dokumentace",
  "/guide": "Životní událost",
  "/production-point": "Výrobní místo",
  "/identification": "Identifikace zákazníka",
  "/tariff": "Tarif",
  "/transfer": "Údaje pro přepis",
  "/new-customer": "Nový zákazník",
  "/claim-payment": "Zaplacení pohledávky",
  "/vehicle": "Vozidlo",
} as const;

export type FormStep = (typeof FORM_STEPS)[keyof typeof FORM_STEPS];

export type FormData = {
  formStep?: FormStep;
  formStepId?: string;
  formType: "eContracts FE";
  productType?: DataLayerProductType;
  product?: string;
  customerType?: DataLayerCustomerType;
  customerSubType?: DataLayerCustomerSubType;
  contractNumber?: string;
  contractId?: string;
  process?: string;
  processValue?: string;
  processReason?: string;
  contractUUID?: string;
  source?: string;
  greenProduct?: boolean;
  formFields?: Record<string, unknown>;
};

export type FormErrorData = FormData & {
  formFieldErrors: Array<Record<string, string>>;
  errorMessage?: string;
  errorId?: string;
  errorTitle?: string;
};

export type FormUpdateData = FormData & {
  updatedFormFields: string[];
  formFieldsValidation: "OK" | "KO" | "N/A";
};

// ─── Event shapes ─────────────────────────────────────────────────────────────

type FormLoadEvent = { event: "formLoad"; formData: FormData };
type FormStartEvent = { event: "formStart"; formData: FormData };
type FormUpdateEvent = { event: "formUpdate"; formData: FormUpdateData };
type FormStepNextEvent = { event: "formStepNext"; formData: FormData };
type FormStepPreviousEvent = { event: "formStepPrevious"; formData: FormData };
type FormSubmitSuccessEvent = { event: "formSubmitSuccess"; formData: FormData };
type FormErrorEvent = { event: "formError"; formData: FormErrorData };
type FormErrorReadEvent = { event: "formErrorRead"; formData: FormErrorData };

export type CtaPosition = "header" | "footer" | "section" | "form" | (string & {});

type CtaClickEvent = {
  event: "ctaClick";
  ctaPosition: CtaPosition;
  ctaDestination: string;
  ctaText: string;
};

type FileDownloadEvent = {
  event: "fileDownload";
  fileName: string;
  fileType: string;
  fileCategory: string;
  fileLocation: string;
};

type ContactClickEvent = {
  event: "contactClick";
  contactType: "Email" | "Phone";
  contactDescription: string;
  contactText: string;
};

type ModalOpenEvent = { event: "modalOpen"; modalName: string };
type ModalCloseEvent = {
  event: "modalClose";
  modalName: string;
  modalButton?: string;
  modalCloseType?: string;
};

type LoaderStartEvent = { event: "loaderStart"; loaderId: string; tsStart: string };
type LoaderFinishedEvent = {
  event: "loaderFinished";
  loaderId: string;
  tsStart: string;
  tsFinished: string;
};

type PageLoadedEvent = { event: "pageLoaded" } & Partial<MandatoryAttributes>;

export type DataLayerEvent =
  | FormLoadEvent
  | FormStartEvent
  | FormUpdateEvent
  | FormStepNextEvent
  | FormStepPreviousEvent
  | FormSubmitSuccessEvent
  | FormErrorEvent
  | FormErrorReadEvent
  | CtaClickEvent
  | FileDownloadEvent
  | ContactClickEvent
  | ModalOpenEvent
  | ModalCloseEvent
  | LoaderStartEvent
  | LoaderFinishedEvent
  | PageLoadedEvent;

// ─── Push function ────────────────────────────────────────────────────────────

// GTM ignores keys pushed with `undefined`, which leaves the previous step's
// value alive in the data layer; an empty string overwrites it and marks the
// attribute as "no value" explicitly.
function emptyStringForUndefined(value: unknown): unknown {
  if (value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map(emptyStringForUndefined);
  }
  if (typeof value === "object" && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, emptyStringForUndefined(entry)]));
  }
  return value;
}

export function pushDataLayer(event: DataLayerEvent) {
  const sanitized = emptyStringForUndefined(event) as DataLayerEvent;
  sendGTMEvent(sanitized);
}

export function pushCtaClick(position: CtaPosition, destination: string, text: string) {
  pushDataLayer({ event: "ctaClick", ctaPosition: position, ctaDestination: destination, ctaText: text });
}

// Recursively collect the visible text of a node so a CTA label is captured
// whether children is a string, an array of nodes, or nested React elements.
export function extractText(node: ReactNode): string {
  if (typeof node === "string") {
    return node;
  }
  if (typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return (node as ReactNode[]).map(extractText).filter(Boolean).join(" ");
  }
  if (isValidElement(node)) {
    return extractText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

export function pushContactClick(contactType: "Email" | "Phone", contactDescription: string, contactText: string) {
  pushDataLayer({ event: "contactClick", contactType, contactDescription, contactText });
}

export function pushModalOpen(modalName: string) {
  pushDataLayer({ event: "modalOpen", modalName });
}

export function pushModalClose(modalName: string, modalButton?: string, modalCloseType?: string) {
  pushDataLayer({ event: "modalClose", modalName, modalButton, modalCloseType });
}

export function pushFileDownload(fileName: string, fileType: string, fileCategory: string, fileLocation: string) {
  pushDataLayer({ event: "fileDownload", fileName, fileType, fileCategory, fileLocation });
}

export function pushFormLoad(formData: FormData) {
  pushDataLayer({ event: "formLoad", formData });
}

export function pushFormStart(formData: FormData) {
  pushDataLayer({ event: "formStart", formData });
}

export function pushFormUpdate(formData: FormUpdateData) {
  pushDataLayer({ event: "formUpdate", formData });
}

export function pushFormStepNext(formData: FormData) {
  pushDataLayer({ event: "formStepNext", formData });
}

export function pushFormStepPrevious(formData: FormData) {
  pushDataLayer({ event: "formStepPrevious", formData });
}

export function pushFormSubmitSuccess(formData: FormData) {
  pushDataLayer({ event: "formSubmitSuccess", formData });
}

export function pushFormError(formData: FormErrorData) {
  pushDataLayer({ event: "formError", formData });
}

export function pushFormErrorRead(formData: FormErrorData) {
  pushDataLayer({ event: "formErrorRead", formData });
}

// ─── Builder helpers ─────────────────────────────────────────────────────────

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const STRIPPED_SEGMENTS = new Set(["new", "draft"]);

export function buildPageName(pathname: string) {
  const cleaned = pathname
    .split("/")
    .filter(seg => !UUID_REGEX.test(seg) && !STRIPPED_SEGMENTS.has(seg))
    .join("/");
  return cleaned || "/";
}

export function buildHierarchyUrl(pathname: string) {
  return buildPageName(pathname).split("/").filter(Boolean);
}

// CTA destination is the current form step only (e.g. "/customer"), without the
// contract UUID or the contract/request prefix that the full pathname carries.
export function buildCtaDestination(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  return lastSegment ? `/${lastSegment}` : "/";
}

export function buildSegment(process?: Process | null, segment?: Segment | null) {
  if (process === Process.VRB) {
    return "VYROBCI";
  }
  if (segment === Segment.B2C) {
    return "MOO";
  }
  if (segment === Segment.B2B) {
    return "MOP";
  }
  return undefined;
}

export function buildProductType(commodity?: Commodity | null) {
  if (commodity === Commodity.Electricity) {
    return "Elektřina";
  }
  if (commodity === Commodity.Gas) {
    return "Plyn";
  }
  return undefined;
}

export function buildProduct(contract?: Contract | null, products?: Product[]) {
  if (!contract || !products) {
    return undefined;
  }
  return getCurrentProductName(contract, products) || undefined;
}

export function buildProcess(processValue?: string | null, processes?: ProcessEnumeration[]) {
  if (!processValue || !processes) {
    return undefined;
  }
  return processes.find(p => String(p.value) === processValue)?.name;
}

export function buildCustomerType(segment?: Segment | null, customerType?: CustomerType | null) {
  if (segment === Segment.B2C) {
    return "MOO";
  }
  if (segment === Segment.B2B) {
    return "MOP";
  }
  if (customerType === "household") {
    return "MOO";
  }
  if (customerType === "tradesman" || customerType === "company") {
    return "MOP";
  }
  return undefined;
}

export function buildCustomerSubType(
  segment?: Segment | null,
  companyType?: CompanyTypeValues | null,
  customerType?: CustomerType | null
) {
  // A contract's segment takes precedence: when it is set we never fall back to
  // the query-derived customerType (which may still hold a stale URL value).
  if (segment) {
    if (segment === Segment.B2B && companyType === CompanyTypeValues.Company) {
      return "Company";
    }
    if (segment === Segment.B2B && companyType === CompanyTypeValues.Tradesman) {
      return "Enterpreneur";
    }
    return undefined;
  }
  if (customerType === "company") {
    return "Company";
  }
  if (customerType === "tradesman") {
    return "Enterpreneur";
  }
  return undefined;
}

export function resolveFormStep(pathname: string | null) {
  if (!pathname) {
    return undefined;
  }
  const segments = pathname.split("/").filter(Boolean);
  const route = `/${segments[segments.length - 1]}`;
  return route in FORM_STEPS ? FORM_STEPS[route as keyof typeof FORM_STEPS] : undefined;
}
